/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import React from 'react';
import { Redirect } from 'react-router-dom';

import AuthLayout from '@layouts/Auth';
import DashboardLayout from '@layouts/Dashboard';
import { getAvailableRoutes } from '@utils/matchRoles';
import { lazyWithRetry } from '@utils/lazyWithRetry';

export const authRoutes = getAvailableRoutes([
  {
    route: '/',
    component: DashboardLayout,
    routes: [
      {
        path: '/',
        exact: true,
        component: () => <Redirect to="/dashboard" />,
      },
      {
        path: '/troubleshooting/workflow_logs',
        exact: true,
        component: lazyWithRetry(() => import('@views/workflow-logs')),
      },
      {
        path: '/troubleshooting/events',
        exact: true,
        component: lazyWithRetry(() => import('@views/troubleshooting')),
      },
      {
        path: '/troubleshooting/sessions',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/troubleshooting-sessions'),
        ),
      },
      {
        path: '/yearly-reports',
        exact: true,
        allowedRoles: ['administrator'],
        component: lazyWithRetry(() =>
          import('@views/client-reports/YearlySessionReports.jsx'),
        ),
      },
      {
        path: '/billing-reports',
        exact: true,
        allowedRoles: ['administrator'],
        component: lazyWithRetry(() =>
          import('@views/client-reports/BillingReport.jsx'),
        ),
      },

      {
        path: '/about',
        exact: true,
        component: lazyWithRetry(() => import('@views/about')),
      },
      {
        path: '/status',
        exact: true,
        allowedRoles: ['administrator'],
        component: lazyWithRetry(() => import('@views/status')),
      },
      {
        path: '/dashboard',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/dashboard/Dashboard'),
        ),
      },
      {
        path: '/dashboard/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/dashboard/Dashboard'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin'],
        notAllowedPermissions: ['!manage_users'],
        path: '/user-management/users',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/user-management/users/UserList'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin'],
        notAllowedPermissions: ['!manage_users'],
        path: '/user-management/endusers',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/user-management/users/EnduserList'),
        ),
      },
      {
        allowedRoles: ['administrator', 'admin'],
        notAllowedPermissions: ['!manage_users'],
        path: '/user-management/online-operators',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/user-management/users/OnlineOperatorsList'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin'],
        notAllowedPermissions: ['!manage_users'],
        path: '/user-management/users/create',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/user-management/users/CreateUser'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin'],
        notAllowedPermissions: ['!manage_users'],
        path: '/user-management/users/:email/:tab?',
        // exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/user-management/users/UserDetails'),
        ),
      },

      {
        allowedRoles: ['administrator'],
        notAllowedPermissions: ['!manage_groups'],
        path: '/user-management/groups',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/user-management/groups/UserGroupList'),
        ),
      },

      {
        allowedRoles: ['administrator'],
        notAllowedPermissions: ['!manage_groups'],
        path: '/user-management/groups/create',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/user-management/groups/CreateUserGroup'),
        ),
      },
      {
        allowedRoles: ['administrator'],
        notAllowedPermissions: ['!manage_groups'],
        path: '/user-management/groups/:groupId/create',
        // exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/user-management/users/CreateUser'),
        ),
      },

      {
        allowedRoles: ['administrator'],
        notAllowedPermissions: ['!manage_groups'],
        path: '/user-management/groups/:groupId/users/:email/:tab?',
        // exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/user-management/users/UserDetails'),
        ),
      },

      {
        allowedRoles: ['administrator'],
        notAllowedPermissions: ['!manage_groups'],
        path: '/user-management/groups/:groupId/:tab?',
        // exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/user-management/groups/UserGroupDetails'),
        ),
      },

      {
        allowedRoles: ['administrator'],
        path: '/configuration/tenants',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/tenants/TenantList'),
        ),
      },

      {
        allowedRoles: ['administrator'],
        path: '/configuration/tenants/create',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/tenants/CreateTenant'),
        ),
      },

      {
        allowedRoles: ['administrator'],
        path: '/configuration/tenants/:tenantId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/tenants/TenantDetails'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin'],
        notAllowedPermissions: ['!manage_workflows'],
        path: '/configuration/workflows',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/workflows/WorkflowList'),
        ),
      },

      {
        allowedRoles: ['administrator'],
        path: '/configuration/workflows/create',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/workflows/CreateWorkflow'),
        ),
      },

      {
        allowedRoles: ['administrator'],
        path: '/troubleshooting/compare-workflows',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/workflows/WorkflowCompare'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin'],
        notAllowedPermissions: ['!manage_workflows'],
        path: '/configuration/workflows/:workflowId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/workflows/WorkflowDetails'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin'],
        notAllowedPermissions: ['!manage_partitions'],
        path: '/configuration/partitions',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/partitions/PartitionsList'),
        ),
      },

      {
        allowedRoles: ['administrator'],
        notAllowedPermissions: ['!manage_partitions'],
        path: '/configuration/partitions/create',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/partitions/CreatePartition'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin'],
        notAllowedPermissions: ['!manage_partitions'],
        path: '/configuration/partitions/:partitionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/partitions/PartitionDetails'),
        ),
      },
      {
        allowedRoles: ['administrator'],
        path: '/configuration/sso-profiles',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/SSOProfiles/SSOProfileList'),
        ),
      },

      {
        allowedRoles: ['administrator'],
        path: '/configuration/sso-profiles/create',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/SSOProfiles/CreateSSOProfile'),
        ),
      },
      {
        allowedRoles: ['administrator'],
        path: '/configuration/sso-profiles/:id/edit',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/SSOProfiles/EditSSOProfile'),
        ),
      },
      {
        allowedRoles: ['administrator'],
        path: '/configuration/sso-profiles/:id',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/configuration/SSOProfiles/EditSSOProfile'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin'],
        path: '/session-management/sessions',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/sessions/SessionList'),
        ),
      },

      {
        allowedRoles: ['operator'],
        allowedPermissions: ['see_all_sessions'],
        path: '/session-management/sessions/supervisor',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/sessions/SessionList'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin'],
        path: '/session-management/sessions/create',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/sessions/CreateSession'),
        ),
      },
      {
        allowedRoles: ['administrator', 'admin', 'operator'],
        path: '/session-management/sessions/bulk-create',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/sessions/BulkCreateSessions'),
        ),
      },
      {
        allowedRoles: ['operator', 'admin', 'administrator'],
        allowedPermissions: ['see_all_sessions'],
        path: '/session-management/sessions/supervisor/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin'],
        path: '/session-management/sessions/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin', 'operator'],
        // allowedPermissions: ['pre_approve_session'],
        path: '/session-management/pre-validation',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/pre-validation/SessionList'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin', 'operator'],
        // allowedPermissions: ['pre_approve_session'],
        path: '/session-management/pre-validation/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin', 'operator'],
        // allowedPermissions: ['pre_approve_session'],
        path: '/session-management/into-pre-validation',
        exact: true,
        component: lazyWithRetry(() =>
          import(
            '@views/main/session-management/into-pre-validation/SessionList'
          ),
        ),
      },

      {
        allowedRoles: ['administrator', 'admin', 'operator'],
        // allowedPermissions: ['pre_approve_session'],
        path: '/session-management/into-pre-validation/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },

      {
        allowedRoles: [
          'administrator',
          'operator',
          'partition_operator',
          'partition_agent',
        ],
        notAllowedPermissions: ['pre_approve_session'],
        path: '/session-management/validation',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/validation/SessionList'),
        ),
      },
      {
        allowedRoles: ['operator'],
        allowedPermissions: ['see_all_sessions'],
        path: '/session-management/supervisor/validation',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/validation/SessionList'),
        ),
      },

      {
        allowedRoles: [
          'administrator',
          'operator',
          'partition_operator',
          'partition_agent',
        ],
        notAllowedPermissions: ['pre_approve_session'],
        path: '/session-management/validation/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },
      {
        allowedRoles: ['operator'],
        allowedPermissions: ['see_all_sessions'],
        path: '/session-management/supervisor/validation/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },

      {
        allowedRoles: [
          'administrator',
          'operator',
          'partition_operator',
          'partition_agent',
        ],
        notAllowedPermissions: ['pre_approve_session'],
        path: '/session-management/validation-in-progress',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/into-validation/SessionList'),
        ),
      },
      {
        allowedRoles: ['operator'],
        allowedPermissions: ['see_all_sessions'],
        path: '/session-management/supervisor/validation-in-progress',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/into-validation/SessionList'),
        ),
      },

      {
        allowedRoles: [
          'administrator',
          'operator',
          'partition_operator',
          'partition_agent',
        ],
        notAllowedPermissions: ['pre_approve_session'],
        path: '/session-management/validation-in-progress/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },
      {
        allowedRoles: ['operator'],
        allowedPermissions: ['see_all_sessions'],
        path: '/session-management/supervisor/validation-in-progress/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },

      {
        allowedRoles: [
          'administrator',
          'operator',
          'partition_operator',
          'partition_agent',
        ],
        notAllowedPermissions: ['pre_approve_session'],
        path: '/session-management/waiting-room',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/waiting-room/SessionList'),
        ),
      },
      {
        allowedRoles: ['operator'],
        allowedPermissions: ['see_all_sessions'],
        path: '/session-management/supervisor/waiting-room',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/waiting-room/SessionList'),
        ),
      },

      {
        allowedRoles: [
          'administrator',
          'admin',
          'operator',
          'partition_operator',
          'partition_agent',
        ],
        notAllowedPermissions: ['pre_approve_session'],
        path: '/session-management/partially-rejected',
        exact: true,
        component: lazyWithRetry(() =>
          import(
            '@views/main/session-management/partially-rejected/SessionList'
          ),
        ),
      },
      {
        allowedRoles: ['operator'],
        allowedPermissions: ['see_all_sessions'],
        path: '/session-management/supervisor/partially-rejected',
        exact: true,
        component: lazyWithRetry(() =>
          import(
            '@views/main/session-management/partially-rejected/SessionList'
          ),
        ),
      },
      {
        allowedRoles: [
          'administrator',
          'admin',
          'operator',
          'partition_operator',
          'partition_agent',
        ],
        path: `/session-management/externally-invalidated`,
        exact: true,
        component: lazyWithRetry(() =>
          import(
            `@views/main/session-management/externally-invalidated/SessionList`
          ),
        ),
      },
      {
        allowedRoles: [
          'administrator',
          'admin',
          'operator',
          'partition_operator',
          'partition_agent',
        ],
        path: '/session-management/rejected',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/rejected/SessionList'),
        ),
      },
      {
        allowedRoles: ['administrator', 'operator', 'partition_agent'],
        notAllowedPermissions: ['hide_completed_sessions'],
        path: '/session-management/completed',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/completed/SessionList'),
        ),
      },

      {
        allowedRoles: ['administrator', 'operator', 'partition_agent'],
        notAllowedPermissions: ['hide_completed_sessions'],
        path: '/session-management/completed/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },
      {
        allowedRoles: [
          'administrator',
          'operator',
          'partition_operator',
          'partition_agent',
        ],
        path: '/session-management/externally-invalidated/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },
      {
        allowedRoles: [
          'administrator',
          'operator',
          'partition_operator',
          'partition_agent',
        ],
        path: '/session-management/rejected/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },
      {
        allowedRoles: [
          'administrator',
          'operator',
          'partition_operator',
          'partition_agent',
        ],
        notAllowedPermissions: ['pre_approve_session'],
        path: '/session-management/partially-rejected/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },
      {
        allowedRoles: ['operator'],
        allowedPermissions: ['see_all_sessions'],
        path: '/session-management/supervisor/partially-rejected/:sessionId/:tab?',
        exact: true,
        component: lazyWithRetry(() =>
          import('@views/main/session-management/session'),
        ),
      },
      {
        path: '/agentroom/:sessionId',
        component: lazyWithRetry(() => import('@views/main/agentroom')),
      },

      {
        path: '/profile/settings',
        exact: true,
        component: lazyWithRetry(() => import('views/main/profile/Settings')),
      },
      {
        path: '/profile/settings/:tab',
        exact: true,
        component: lazyWithRetry(() => import('views/main/profile/Settings')),
      },

      {
        component: () => <Redirect to="/" />,
      },
    ],
  },
]);

export const unAuthRoutes = getAvailableRoutes([
  {
    path: '/',
    component: AuthLayout,
    routes: [
      {
        path: '/',
        exact: true,
        component: lazyWithRetry(() => import('@views/unauth/Login')),
      },
      {
        path: '/about',
        exact: true,
        component: lazyWithRetry(() => import('@views/about')),
      },
      {
        path: '/password/forgot',
        exact: true,
        component: lazyWithRetry(() => import('@views/unauth/ForgotPassword')),
      },
      {
        path: '/password/reset/:token/:email',
        exact: true,
        component: lazyWithRetry(() => import('@views/unauth/ResetPassword')),
      },
      {
        path: '/auth/microsoft',
        exact: true,
        component: lazyWithRetry(() => import('@views/unauth/MicrosoftAuth')),
      },
      {
        path: '/auth/openid',
        exact: true,
        component: lazyWithRetry(() => import('@views/unauth/OpenIdAuth')),
      },
      {
        path: '/auth/openiddict',
        exact: true,
        component: lazyWithRetry(() => import('@views/unauth/OpenIddict')),
      },
      {
        path: '/auth/idsign',
        exact: true,
        component: lazyWithRetry(() => import('@views/unauth/IdSignAuth')),
      },
      {
        component: () => {
          // Save current path to sessionStorage before redirecting
          const currentPath = window.location.pathname + window.location.search;
          if (currentPath !== '/') {
            sessionStorage.setItem('redirectPath', currentPath);
          }
          return <Redirect to="/" />;
        },
      },
    ],
  },
]);
